import { cardBrandLabel } from '@/features/user/settings/sections/plan-change/lib/helpers'
import type {
  CardSummary,
  PaymentMethodUpdateError,
} from './payment-method.types'

export type PaymentMethodFlow = 'sheet' | 'account'

interface ErrorCopy {
  title: string
  description?: string
}

export const cardNameLabel = (card: CardSummary): string =>
  `${cardBrandLabel(card.brand) ?? 'Card'} ending in ${card.last4}`

export const cardExpiryLabel = (card: CardSummary): string =>
  `Expires ${String(card.expMonth).padStart(2, '0')}/${card.expYear}`

export const savedCopy = (card: CardSummary | null): string =>
  card ? `Card saved. ${cardNameLabel(card)} is now on file.` : 'Card saved.'

const stillOnFile = (
  card: CardSummary | null,
  remedy: string
): string | undefined =>
  card ? `${cardNameLabel(card)} is still on file. ${remedy}` : undefined

const SHEET_UNCHANGED = "Nothing was charged and your plan hasn't changed."

export const errorCopyFor = ({
  flow,
  error,
  card,
  priorDecline = false,
}: {
  flow: PaymentMethodFlow
  error: PaymentMethodUpdateError
  card: CardSummary | null
  priorDecline?: boolean
}): ErrorCopy => {
  switch (error) {
    case 'load_failed':
      return { title: "We couldn't load the card form. Try again." }
    case 'finalize_failed':
      return {
        title:
          "Your card was saved, but we couldn't confirm it yet. Refresh in a minute.",
      }
    case 'authentication':
      return {
        title: "Your bank didn't approve this card",
        description:
          flow === 'sheet'
            ? `${SHEET_UNCHANGED} Try again or use a different card.`
            : stillOnFile(card, 'Try again or use a different card.'),
      }
    case 'declined':
      if (flow === 'account') {
        return {
          title: 'This card was declined',
          description: stillOnFile(card, 'Try a different card.'),
        }
      }
      return {
        title: priorDecline
          ? 'This card was declined too'
          : 'This card was declined',
        description: `${SHEET_UNCHANGED} Check that your card info was entered correctly, or try a different card.`,
      }
    case 'generic':
      return {
        title: "We couldn't save this card",
        description: 'Nothing was charged. Try again in a moment.',
      }
    default: {
      const exhaustive: never = error
      throw new Error(`Unhandled payment method error: ${String(exhaustive)}`)
    }
  }
}
